// src/pages/About.jsx
import { useEffect, useState } from "react";
import Aos from "aos";
import "aos/dist/aos.css";
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from "@mui/material/CircularProgress";

import Preference from "../assets/Preference.png";
import Vision from "../assets/Vision.png";
import FillBlank from "../assets/FillBlank.png";
import Value from "../assets/Value.png";
import Time from "../assets/Time.png";
// import Environmental from "../assets/Environmental.png";

import AboutPart from "../components/AboutPart";

export default function About() {
    useEffect(() => {
        Aos.init({ duration: 1500, once: true });
    }, []);

    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [updateMessage, setUpdateMessage] = useState('');

    const handleClose = () => setOpen(false);

    const handleSubmitContact = async (e) => {
        e.preventDefault();
        const formData = {
            name: e.target.name.value.trim(),
            email: e.target.email.value.trim(),
            phone: e.target.phone.value.trim(),
            message: e.target.message.value.trim(),
        };

        if (!formData.name || !formData.email || !formData.phone || !formData.message) {
            setOpen(true);
            setUpdateMessage("Please fill out all fields before submitting.");
            return;
        }

        try {
            setLoading(true);
            const res = await fetch("https://fixerfind-web2-backend.vercel.app/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const data = await res.json();
            if (!res.ok) setUpdateMessage(data.error || "Something went wrong.");
        } catch (err) {
            setUpdateMessage("Something went wrong. Please try again.");
        } finally {
            setOpen(true);
            setLoading(false);
            setUpdateMessage("Graciously received! - We'll reply you via email shortly.");
            e.target.reset();
        }
    };

    return (
        <div className="relative bg-gray-50 text-gray-900">
            {/* Hero Section */}
            <section data-aos="fade-up" className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24 py-28">
                <h1 className="text-4xl md:text-5xl font-extrabold text-blue-600 mb-8">About FixerFind</h1>

                <div className="md:flex md:items-start md:gap-12">
                    <div className="md:flex-1 space-y-4 text-gray-700">
                        <p>
                            FixerFind is a people-first service marketplace that instantly connects homes, businesses,
                            and communities with trustworthy local professionals. We combine human verification,
                            clear pricing, and an easy booking flow to make getting help fast and reliable.
                        </p>
                        <p>
                            Our platform is built on speed, transparency, and safety. With more like {" "}
                            <strong>FES-AI (Fast & Easy Service — Artificial Intelligence)</strong> in the building stage,
                            Premium users get smarter matching, scheduling, predictive arrival times,
                            and data-driven insights.
                        </p>
                        {/* <p>
                            Founded in Nigeria, FixerFind started as a small team solving everyday service headaches.
                            Today we’re growing — always focused on practical reliability, local impact,
                            and meaningful outcomes for both customers and the skilled offering hands.
                        </p> */}
                    </div>
                    <div className="mt-6 md:mt-0 md:w-96 relative">
                        <img
                            src={FillBlank}
                            alt="FixerFind team"
                            className="rounded-3xl shadow-2xl w-full transform transition-transform hover:scale-105"
                        />
                        <div className="absolute -top-6 -left-6 w-28 h-28 bg-purple-500/30 rounded-full filter blur-3xl animate-pulse" />
                        <div className="absolute -bottom-6 -right-6 w-36 h-36 bg-blue-500/20 rounded-full filter blur-3xl animate-pulse" />
                    </div>
                </div>
            </section>

            {/* Mission / Vision / Core Values */}
            <section className="bg-white py-20 px-6 md:px-12 lg:px-24">
                <div className="max-w-6xl mx-auto grid gap-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-3xl font-bold text-blue-600 mb-3">Our Mission</h2>
                        <p className="text-gray-700">
                            Make essential everyday services easy and trustworthy—helping people secure dependable, high-quality assistance while supporting local professionals with fair opportunities.
                        </p>
                    </div>
                    <div data-aos="fade-up" data-aos-delay="100">
                        <h2 className="text-3xl font-bold text-blue-600 mb-3">Our Vision</h2>
                        <p className="text-gray-700">
                            To build the region’s most trusted network for essential services—where technology and local expertise combine to deliver faster results and sustainable livelihoods.
                        </p>
                    </div>
                    <div data-aos="fade-up" data-aos-delay="200">
                        <h2 className="text-3xl font-bold text-blue-600 mb-3">Core Values</h2>
                        <ul className="list-disc pl-5 space-y-2 text-gray-700">
                            <li><strong>Trust:</strong> Verified providers you can rely on</li>
                            <li><strong>Speed:</strong> Quick matches and hassle-free payments</li>
                            <li><strong>Fairness:</strong> Clear pricing and fair pay</li>
                            <li><strong>Safety:</strong> Privacy and verification built in</li>
                        </ul>

                    </div>
                </div>
            </section>


            {/* AboutPart Component */}
            <div className="py-16 px-6 md:px-12 lg:px-24 bg-white rounded-3xl my-8 shadow">
                <AboutPart />
            </div>

            {/* Briefing Sections */}
            <section className="max-w-6xl mx-auto px-6 py-20 space-y-20">
                <h2 className="text-4xl font-bold text-blue-600 text-center mb-12">Briefing</h2>

                {[
                    { title: "Our Rates", text: "We balance customer affordability with fair provider earnings. Standard usage is free. Premium plans unlock priority booking, FES-AI features, and promotions.", img: Preference },
                    { title: "Continuous Development", text: "We iterate quickly — listening to users, monitoring outcomes, and launching improvements regularly. Security, reliability, and simplicity guide every release.", img: Time, reverse: true },
                    { title: "Human-centered Technology", text: "FixerFind uses data and AI to simplify human tasks — not replace the human touch. Our design prioritizes clear choices and privacy-by-default.", img: Value },
                    { title: "Assessment & Growth", text: "We give providers tools to grow: booking data, feedback loops, and actionable suggestions. Customers benefit from higher-quality matches.", img: Vision, reverse: true },
                ].map((section, idx) => (
                    <div data-aos="fade-up" key={idx} className={`md:flex md:items-center md:gap-12 ${section.reverse ? 'md:flex-row-reverse' : ''}`}>
                        <div className="md:w-1/2 space-y-4">
                            <h3 className="text-2xl font-semibold text-blue-500 mb-3">{section.title}</h3>
                            <p className="text-gray-700">{section.text}</p>
                        </div>
                        <div className="md:w-1/2 mt-6 md:mt-0 relative flex justify-center">
                            <img
                                src={section.img}
                                alt={section.title}
                                className="w-full max-w-md rounded-3xl shadow-2xl hover:scale-105 transition object-contain"
                            />
                            <div className="absolute -top-6 -left-6 w-20 h-20 bg-purple-500/20 rounded-full filter blur-3xl animate-pulse" />
                            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-500/20 rounded-full filter blur-3xl animate-pulse" />
                        </div>
                    </div>
                ))}
            </section>

            {/* Trusted By Section */}
            <section className="bg-gray-50 text-gray-900 py-20 px-6 md:px-12 lg:px-24 text-center">
                <h2 className="text-3xl font-bold text-blue-600 mb-12">Trusted by</h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-6">
                    {["Local partners", "Fintech Payments", "Provider network", "Community pilots", "Travel Agencies", "Logistic Teams"].map((partner, idx) => (
                        <blockquote key={idx} className="p-6 bg-white rounded-xl shadow hover:shadow-lg transition">{partner}</blockquote>
                    ))}
                </div>
            </section>

            {/* Contact Section */}
            <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 bg-gray-50">
                <h2 className="text-3xl font-bold text-blue-600 mb-6">
                    Contact & Partnerships
                </h2>

                <p className="text-gray-700 mb-8">
                    Want to partner, invest, or pilot FixerFind in your city? Reach out — we welcome conversations with local partners, payment providers, and community organizations.
                </p>

                <form className="grid gap-4 max-w-lg mx-auto" onSubmit={handleSubmitContact}>
                    <input name="name" type="text" placeholder="Your Name" className="p-3 border rounded-lg text-gray-900" minLength={2} />
                    <input name="email" type="email" placeholder="Your Email" className="p-3 border rounded-lg text-gray-900" />
                    <input name="phone" type="tel" placeholder="Your Phone Number" className="p-3 border rounded-lg text-gray-900" minLength={7} maxLength={15} />
                    <textarea name="message" placeholder="How can we collaborate?" rows="4" className="p-3 border rounded-lg text-gray-900" minLength={5}></textarea>
                    <button
                        type="submit"
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Send Message"}
                    </button>
                    <Snackbar
                        open={open}
                        autoHideDuration={6000}
                        onClose={handleClose}
                        message={updateMessage}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    />
                </form>

                <p className="text-gray-400 mt-4 text-center">
                    Or email us at{" "}
                    <a href="mailto:hello@fixerfind.com" className="text-blue-600 hover:underline">
                        hello@fixerfind.com
                    </a>
                </p>
            </section>
        </div>
    );
}
